import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { CoverageNotesMap } from "./types";
import {
  coverageNoteQueryKeys,
  resolveNoteKey,
  useCoverageNotes,
} from "./useCoverageNoteHooks";

const readCachedCoverageNotes = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): CoverageNotesMap =>
  queryClient.getQueryData<CoverageNotesMap>(queryKey) ?? {};

const buildCoverageNotesMap = (
  coverages: { id: string; benefitPlanType?: string; noteKey?: string }[],
  primaryNotes: CoverageNotesMap,
  fallbackNotes: CoverageNotesMap = {},
): CoverageNotesMap =>
  coverages.reduce<CoverageNotesMap>((acc, coverage) => {
    const key = resolveNoteKey(coverage);
    if (key in primaryNotes) {
      acc[key] = primaryNotes[key];
      return acc;
    }

    acc[key] = fallbackNotes[key] ?? "";
    return acc;
  }, {});

interface UseCoverageNoteDraftsOptions {
  ecoRfpId: string;
  coverages: { id: string; benefitPlanType?: string; noteKey?: string }[];
  enabled?: boolean;
}

type CoverageNotesDraftsUpdater =
  | CoverageNotesMap
  | ((previousDrafts: CoverageNotesMap) => CoverageNotesMap);

interface UseCoverageNoteDraftsResult {
  notes: CoverageNotesMap;
  baselineNotes: CoverageNotesMap;
  isLoading: boolean;
  isError: boolean;
  setCoverageNote: (coverageId: string, content: string) => void;
  setCoverageNotes: (updater: CoverageNotesDraftsUpdater) => void;
  resetCoverageNotes: () => void;
}

export const useCoverageNoteDrafts = ({
  ecoRfpId,
  coverages,
  enabled = true,
}: UseCoverageNoteDraftsOptions): UseCoverageNoteDraftsResult => {
  const queryClient = useQueryClient();
  const draftNotesQueryKey = useMemo(
    () => coverageNoteQueryKeys.draftCoverageNotes(ecoRfpId),
    [ecoRfpId],
  );
  const cachedBaselineNotesQueryKey = useMemo(
    () => coverageNoteQueryKeys.baselineCoverageNotes(ecoRfpId),
    [ecoRfpId],
  );

  const {
    data: serverBaselineNotes,
    isLoading,
    isError,
  } = useCoverageNotes({
    ecoRfpId,
    coverages,
    enabled,
  });

  const { data: draftNotes = {} } = useQuery({
    queryKey: draftNotesQueryKey,
    queryFn: () => readCachedCoverageNotes(queryClient, draftNotesQueryKey),
    initialData: () => readCachedCoverageNotes(queryClient, draftNotesQueryKey),
    staleTime: Infinity,
  });

  const { data: cachedBaselineNotes = {} } = useQuery({
    queryKey: cachedBaselineNotesQueryKey,
    queryFn: () =>
      readCachedCoverageNotes(queryClient, cachedBaselineNotesQueryKey),
    initialData: () =>
      readCachedCoverageNotes(queryClient, cachedBaselineNotesQueryKey),
    staleTime: Infinity,
  });

  const resolvedBaselineNotes = useMemo(() => {
    if (!serverBaselineNotes && Object.keys(cachedBaselineNotes).length === 0) {
      return {};
    }

    return buildCoverageNotesMap(
      coverages,
      cachedBaselineNotes,
      serverBaselineNotes,
    );
  }, [cachedBaselineNotes, coverages, serverBaselineNotes]);

  const mergedNotes = useMemo(
    () => buildCoverageNotesMap(coverages, draftNotes, resolvedBaselineNotes),
    [coverages, draftNotes, resolvedBaselineNotes],
  );

  const setCoverageNote = useCallback(
    (coverageId: string, content: string) => {
      queryClient.setQueryData<CoverageNotesMap>(
        draftNotesQueryKey,
        (previousDrafts) => ({
          ...(previousDrafts ?? {}),
          [coverageId]: content,
        }),
      );
    },
    [draftNotesQueryKey, queryClient],
  );

  const setCoverageNotes = useCallback(
    (updater: CoverageNotesDraftsUpdater) => {
      queryClient.setQueryData<CoverageNotesMap>(
        draftNotesQueryKey,
        (previousDrafts) => {
          const previousDraftsMap = previousDrafts ?? {};
          const previousCoverageDrafts = coverages.reduce<CoverageNotesMap>(
            (acc, coverage) => {
              const key = resolveNoteKey(coverage);
              if (key in previousDraftsMap) {
                acc[key] = previousDraftsMap[key];
              }

              return acc;
            },
            {},
          );
          const nextCoverageDrafts =
            typeof updater === "function"
              ? updater(previousCoverageDrafts)
              : updater;

          const nextDraftsSnapshot = { ...previousDraftsMap };

          for (const coverage of coverages) {
            const key = resolveNoteKey(coverage);
            if (key in nextCoverageDrafts) {
              nextDraftsSnapshot[key] = nextCoverageDrafts[key];
            } else {
              delete nextDraftsSnapshot[key];
            }
          }

          return nextDraftsSnapshot;
        },
      );
    },
    [coverages, draftNotesQueryKey, queryClient],
  );

  const resetCoverageNotes = useCallback(() => {
    queryClient.setQueryData<CoverageNotesMap>(
      draftNotesQueryKey,
      (previousDrafts) => {
        const nextDrafts = { ...(previousDrafts ?? {}) };

        for (const coverage of coverages) {
          const key = resolveNoteKey(coverage);
          nextDrafts[key] = resolvedBaselineNotes[key] ?? "";
        }

        return nextDrafts;
      },
    );
  }, [coverages, draftNotesQueryKey, queryClient, resolvedBaselineNotes]);

  return {
    notes: mergedNotes,
    baselineNotes: resolvedBaselineNotes,
    isLoading,
    isError,
    setCoverageNote,
    setCoverageNotes,
    resetCoverageNotes,
  };
};
